import React, { useState, useEffect } from "react";
import { User, LoginResponse } from "../types";
import { useTranslation } from "react-i18next";
import { deleteAccount, getProfile } from "../api/auth";
import ChangePasswordModal from "./ChangePasswordModal";
import {
  User as UserIcon,
  Home,
  Edit,
  Camera,
  ShieldCheck,
  Bell,
  LogOut,
  KeyRound,
  HelpCircle,
  HeartPulse,
  Trash2,
} from "lucide-react";
import { ConfirmDialog } from "./ConfirmDialog";
import { use } from "i18next";
import { toast } from "sonner";

interface ProfileProps {
  setCurrentPage: (
    page:
      | "menu"
      | "cart"
      | "checkout"
      | "tracking"
      | "service"
      | "legal"
      | "profile"
      | "update-profile",
  ) => void;
  onLogout: () => void;
}

const ProfileSkeleton: React.FC = () => (
  <div className="bg-gray-50 min-h-screen animate-pulse">
    <div className="container mx-auto p-4 sm:p-6 lg:p-8">
      <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1 space-y-8">
          <div className="bg-white shadow-lg rounded-xl p-6 text-center">
            <div className="w-32 h-32 bg-gray-200 rounded-full mx-auto mb-4"></div>
            <div className="h-6 bg-gray-200 rounded w-3/4 mx-auto mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
          </div>
          <div className="bg-white shadow-lg rounded-xl p-6">
            <div className="h-8 bg-gray-200 rounded mb-2"></div>
            <div className="h-8 bg-gray-200 rounded mb-2"></div>
            <div className="h-8 bg-gray-200 rounded"></div>
          </div>
        </div>
        <div className="lg:col-span-2 space-y-8">
          <div className="bg-white shadow-lg rounded-xl p-6">
            <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-full mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-full mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-full"></div>
          </div>
          <div className="bg-white shadow-lg rounded-xl p-6">
            <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const Profile: React.FC<ProfileProps> = ({ setCurrentPage, onLogout }) => {
  const { t } = useTranslation();
  const [user, setUser] = useState<User | null>(null);
  const [picture, setPicture] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSecurityOpen, setIsSecurityOpen] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  const handleConfirmDeleteAccount = async () => {
    setIsDeletingAccount(true);
    const response = await deleteAccount(user?.id || "");

    if (response.error) {
      toast.error(response.error.message);
    } else {
      window.location.href = "/";
    }
  };

  useEffect(() => {
    const fetchAllData = async () => {
      setIsLoading(true);

      const simple = localStorage.getItem("user");
      const simpleUserData: LoginResponse | null = simple
        ? JSON.parse(simple)
        : null;
      // if (simpleUserData?.picture) {
      //   setPicture(simpleUserData.picture);
      // }
      const userId = simpleUserData?.id;
      if (!userId) {
        setError("User ID not found. Please log in.");
        setIsLoading(false);
        return;
      }

      const profileResponse = await getProfile(userId);

      console.log("profile response ", profileResponse);

      if (profileResponse.error) {
        setError(profileResponse.error.message);
      } else {
        setPicture(profileResponse?.data?.profileImageUrl ?? null);
        setUser(profileResponse.data);
      }

      setIsLoading(false);
    };

    fetchAllData();
  }, []);

  const renderTags = (
    text: string | string[] | undefined | null,
    noTextKey: string,
  ) => {
    if (!text) {
      return (
        <p className="text-gray-500 italic">{t(noTextKey, "None specified")}</p>
      );
    }

    let items: string[] = [];
    if (typeof text === "string") {
      items = text
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    } else if (Array.isArray(text)) {
      items = text.map((item) => String(item).trim()).filter(Boolean);
    }

    if (items.length === 0) {
      return (
        <p className="text-gray-500 italic">{t(noTextKey, "None specified")}</p>
      );
    }

    return (
      <div className="flex flex-wrap gap-2 mt-2">
        {items.map((item, index) => (
          <span
            key={index}
            className="bg-orange-100 text-orange-800 text-sm font-medium px-2.5 py-0.5 rounded-full"
          >
            {item}
          </span>
        ))}
      </div>
    );
  };

  if (isLoading) {
    return <ProfileSkeleton />;
  }

  if (error) {
    return (
      <div className="container mx-auto p-4 text-center text-red-600">
        <h1 className="text-2xl font-bold mb-4">{t("profile.title")}</h1>
        <p>{error}</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="container mx-auto p-4 text-center text-gray-600">
        <h1 className="text-2xl font-bold mb-4">{t("profile.title")}</h1>
        <p>{t("profile.notLoggedIn")}</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="container mx-auto p-4 sm:p-6 lg:p-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800">
            {t("profile.title")}
          </h1>
          <button
            onClick={() => setCurrentPage("update-profile")}
            className="flex items-center space-x-2 bg-orange-500 text-white px-4 py-2 rounded-lg shadow-md hover:bg-orange-600 transition-colors"
          >
            <Edit className="w-4 h-4" />
            <span>{t("profile.editProfile")}</span>
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1 space-y-8">
            <div className="bg-white shadow-lg rounded-xl p-6 text-center">
              <div className="relative w-32 h-32 mx-auto mb-4">
                <img
                  src={
                    picture ||
                    `https://ui-avatars.com/api/?name=${user.firstName}+${user.lastName}&background=random`
                  }
                  alt="Profile"
                  className="w-full h-full object-cover rounded-full shadow-md"
                />
                <button className="absolute bottom-0 right-0 bg-orange-500 text-white p-2 rounded-full hover:bg-orange-600 transition-colors shadow-md">
                  <Camera className="w-4 h-4" />
                </button>
              </div>
              <h2 className="text-2xl font-bold text-gray-800">
                {user.firstName} {user.lastName}
              </h2>
              <p className="text-gray-500">{user.email}</p>
            </div>

            <div className="bg-white shadow-lg rounded-xl p-6">
              <ul className="space-y-2">
                <li>
                  <button
                    onClick={() => setIsSecurityOpen(!isSecurityOpen)}
                    className="w-full flex justify-between items-center text-gray-700 hover:text-orange-500 hover:bg-gray-50 p-2 rounded-md transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      <ShieldCheck className="w-5 h-5 text-gray-400" />
                      <span>{t("profile.security", "Security")}</span>
                    </div>
                    <svg
                      className={`w-5 h-5 transform transition-transform ${
                        isSecurityOpen ? "rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 9l-7 7-7-7"
                      ></path>
                    </svg>
                  </button>
                  {isSecurityOpen && (
                    <div className="pl-8 pt-2 space-y-2">
                      <button
                        onClick={() => setShowChangePasswordModal(true)}
                        className="w-full text-left flex items-center space-x-3 text-gray-600 hover:text-orange-500 hover:bg-gray-50 p-2 rounded-md transition-colors"
                      >
                        <KeyRound className="w-5 h-5 text-gray-400" />
                        <span>
                          {t("profile.changePassword", "Change Password")}
                        </span>
                      </button>
                      <button
                        onClick={() => {
                          setConfirmDialogOpen(true);
                        }}
                        className="w-full text-left flex items-center space-x-3 text-red-600 hover:text-red-500 hover:bg-gray-50 p-2 rounded-md transition-colors"
                      >
                        <Trash2 className="w-5 h-5 text-gray-400" />
                        <span>{t("profile.deleteAccount")}</span>
                      </button>
                      {/* <a href="#" className="flex items-center space-x-3 text-gray-600 hover:text-orange-500 hover:bg-gray-50 p-2 rounded-md transition-colors">
                                                <HelpCircle className="w-5 h-5 text-gray-400" />
                                                <span>{t('profile.forgotPassword', 'Forgot Password')}</span>
                                            </a> */}
                    </div>
                  )}
                </li>
                {/* <li><a href="#" className="flex items-center space-x-3 text-gray-700 hover:text-orange-500 hover:bg-gray-50 p-2 rounded-md transition-colors"><Bell className="w-5 h-5 text-gray-400" /><span>{t('profile.notifications', 'Notifications')}</span></a></li> */}
                <li>
                  <button
                    onClick={onLogout}
                    className="flex items-center space-x-3 text-red-500 hover:bg-red-50 p-2 rounded-md transition-colors w-full text-left"
                  >
                    <LogOut className="w-5 h-5" />
                    <span>{t("profile.logout", "Logout")}</span>
                  </button>
                </li>
              </ul>
            </div>
          </div>

          <div className="lg:col-span-2 space-y-8">
            <div className="bg-white shadow-lg rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <UserIcon className="w-5 h-5 mr-2 text-orange-500" />
                {t("profile.personalInfo")}
              </h3>
              <div className="space-y-4">
                <div className="flex items-center border-b pb-4">
                  <p className="w-1/3 text-gray-500">
                    {t("auth.fullName", "Full Name")}
                  </p>
                  <p className="w-2/3 text-gray-800 font-semibold">
                    {user.firstName} {user.lastName}
                  </p>
                </div>
                <div className="flex items-center border-b pb-4">
                  <p className="w-1/3 text-gray-500">
                    {t("auth.emailAddress")}
                  </p>
                  <p className="w-2/3 text-gray-800 font-semibold">
                    {user.email}
                  </p>
                </div>
                <div className="flex items-center">
                  <p className="w-1/3 text-gray-500">{t("auth.phone")}</p>
                  <p className="w-2/3 text-gray-800 font-semibold">
                    {user.phone}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white shadow-lg rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <Home className="w-5 h-5 mr-2 text-orange-500" />
                {t("profile.deliveryAddress")}
              </h3>
              {user?.address ? (
                <div className="text-gray-800 space-y-1">
                  <p>{user.address.street}</p>
                  <p>
                    {user.address.city}, {user.address.zipCode}
                  </p>
                  <p>{user.address.country}</p>
                </div>
              ) : (
                <p className="text-gray-500">
                  {t("profile.noAddress", "No address provided.")}
                </p>
              )}
            </div>

            <div className="bg-white shadow-lg rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <HeartPulse className="w-5 h-5 mr-2 text-orange-500" />
                {t("profile.healthAndPreferences")}
              </h3>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-500">
                    {t("profile.allergies")}
                  </p>
                  {renderTags(user.allergies, "profile.noAllergies")}
                </div>
                <div className="border-t pt-4 mt-4">
                  <p className="text-sm text-gray-500">
                    {t("profile.preferences")}
                  </p>
                  {renderTags(user.preferences, "profile.noPreferences")}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showChangePasswordModal && (
        <ChangePasswordModal
          onClose={() => setShowChangePasswordModal(false)}
        />
      )}
      <ConfirmDialog
        open={confirmDialogOpen}
        type="warning"
        title={t("profile.deleteAccount")}
        description={t("profile.confirmDeleteAccount")}
        confirmText={t("profile.delete")}
        cancelText={t("profile.cancel")}
        onConfirm={() => handleConfirmDeleteAccount()}
        onCancel={() => setConfirmDialogOpen(false)}
        isLoading={isDeletingAccount}
      ></ConfirmDialog>
    </div>
  );
};

export default Profile;
